import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayers, queriesPayrollSchedules } from "@query";
import {
  toWSDateString,
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSForm,
  WSGrid,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayerPayrollStatusUpdate,
  PayrollScheduleType,
  RecurrenceScheduleDate
} from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import * as Yup from "yup";
import {
  FieldsPayrollCadence,
  payrollCadenceValidationSchema
} from "../../../shared/components/FieldsPayrollCadence";
import { PayrollCadenceFormData } from "../../../shared/components/FieldsPayrollCadence/types";
import { selectorPayrollCadence } from "../../../shared/selectors/selectorPayrollCadence";
import { selectorPayrollCadenceFormData } from "../../../shared/selectors/selectorPayrollCadenceFormData";
import { selectorPayrollScheduleUpdate } from "../../../shared/selectors/selectorPayrollScheduleUpdate";
import { PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD } from "../../../modules/Settings/paths";
import { EmptyState } from "../../../shared/components/EmptyState";
import { getExactCutOffDate } from "../../utils/payables";

/**
 * Contractor payroll schedule settings, on v3.
 *
 * Two v3 resources back this screen, and they are deliberately not the same one:
 *
 * - **the cadence and the upcoming runs** live on `PayrollSchedule`
 *   (`/v3/payments/payroll-schedules`), the resource v3 designates for both. It
 *   is addressed by type rather than by id, and projects `scheduleDates`
 *   read-only. `PayerSettings` declares no 1099 `frequency` at all.
 * - **the payroll on/off switch** lives on `PayerSettings`
 *   (`/v3/payments/payer-settings`), which accepts `runsPayroll` on its PATCH.
 *
 * Both resources serve and accept writes against a real payer session
 * (`payrollSchedules.itest.ts`): the cadence read returns `frequency` /
 * `interval` / `dayOfWeek`, and a `PATCH` of the three regenerates
 * `scheduleDates`. The payer-settings write is elevated — every
 * `payrollSettings` body, `runsPayroll` included, answers `403
 * StepUpMfaRequired` for `PayoutMethodChange`, which `mfaChallengeMiddleware`
 * prompts for and retries.
 *
 * The cadence fields and their two mappings are shared with the payer-onboarding
 * payroll-settings step — see `FieldsPayrollCadence`. See
 * V3_MIGRATION_TRACKER.md § payer-settings for what this screen lost on v3
 * (approval strategy, 1099 policy, the Wingspan-managed day-count toggles, and
 * moving or skipping a single run).
 */

const ModalDisablePayroll: React.FC<WSModalCustomProps<boolean>> = props => (
  <WSModal
    title="Are you sure?"
    size="S"
    onClose={() => props.onClose(false)}
    actions={{
      buttons: [
        {
          kind: "Tertiary",
          label: "Cancel",
          onClick: () => props.onClose(false)
        },
        {
          kind: "Primary",
          destructive: true,
          label: "Yes, disable payroll",
          onClick: () => props.onClose(true)
        }
      ]
    }}
  >
    <WSAlert
      theme="warning"
      icon="alert-circle"
      title="Disable payroll and remove upcoming payroll runs"
    >
      If you proceed with disabling payroll now, all upcoming payroll runs will
      be removed. You can always re-enable it and set up your payroll runs from
      scratch.
    </WSAlert>
  </WSModal>
);

const useModalDisablePayroll = () => useWSModalCustom(ModalDisablePayroll);

const ModalConfirmScheduleChange: React.FC<
  WSModalCustomProps<boolean>
> = props => (
  <WSModal
    title="Confirm saving"
    size="S"
    onClose={() => props.onClose(false)}
    actions={{
      buttons: [
        {
          kind: "Secondary",
          label: "Cancel",
          onClick: () => props.onClose(false)
        },
        { label: "Confirm", onClick: () => props.onClose(true) }
      ]
    }}
  >
    <WSText.ParagraphSm color="gray600">
      All future payroll runs will be rescheduled to match the new cadence.
    </WSText.ParagraphSm>
  </WSModal>
);

const useModalConfirmScheduleChange = () =>
  useWSModalCustom(ModalConfirmScheduleChange);

export const PayrollSettings: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { openSnackbar } = useWSSnackbar();
  const modalDisablePayroll = useModalDisablePayroll();
  const modalConfirmScheduleChange = useModalConfirmScheduleChange();

  const queryPayerSettings = queriesPayers.getCurrentPayerSettings.useQuery();
  const querySchedules = queriesPayrollSchedules.listPayrollSchedules.useQuery(
    { filter: { type: { eq: PayrollScheduleType.CONTRACTOR_VENDOR } } },
    // A payer with no schedule of this type has none to read and none this
    // screen can create, so retrying only delays the point at which the form can
    // admit it has no saved cadence to show.
    { retry: false }
  );

  const [updatePayerSettings, updatePayerSettingsMeta] =
    queriesPayers.updateCurrentPayerSettings.useMutation();
  const [updateSchedule, updateScheduleMeta] =
    queriesPayrollSchedules.updatePayrollSchedule.useMutation();

  const payrollSettings = queryPayerSettings.data?.payrollSettings;
  const schedule = querySchedules.data?.data?.[0];

  // A schedule can come back with its pay dates but without the frequency and
  // interval that produced them; inverting the dates recovers the same cadence —
  // see selectorPayrollCadence.
  const derivedCadence = selectorPayrollCadence(schedule?.scheduleDates);

  const setRunsPayroll = useCallback(
    async (runsPayroll: boolean) => {
      await updatePayerSettings(
        {
          currentPayerSettingsUpdate: {
            payrollSettings: runsPayroll
              ? // Enabling also activates payroll, as the v1 write did: a payer
                // whose settings sit at Draft would otherwise have the switch on
                // and no payroll running.
                { runsPayroll: true, status: PayerPayrollStatusUpdate.ACTIVE }
              : { runsPayroll: false }
          }
        },
        {
          onSuccess: () => {
            openSnackbar({
              type: "success",
              message: runsPayroll ? "Payroll enabled" : "Payroll disabled"
            });
          }
        }
      );
    },
    [openSnackbar, updatePayerSettings]
  );

  const handleSubmit = useCallback(
    async (data: PayrollCadenceFormData) => {
      // Changing the cadence rebuilds every future run, so the payer confirms
      // once before the write rather than discovering it from the table after.
      if (!(await modalConfirmScheduleChange.open({}))) {
        return;
      }

      // The schedule is addressed by its type, not by an id the caller has to
      // hold, so this reaches the payer's contractor schedule whether or not the
      // read above could serve it. There is no create verb at all — a payer with
      // no contractor schedule has nothing this screen can make.
      await updateSchedule(
        {
          type: PayrollScheduleType.CONTRACTOR_VENDOR,
          payrollScheduleUpdate: selectorPayrollScheduleUpdate(data)
        },
        {
          onSuccess: () => {
            openSnackbar({
              type: "success",
              message: "Payroll schedule updated"
            });
          }
        }
      );
    },
    [modalConfirmScheduleChange, openSnackbar, updateSchedule]
  );

  // The cadence read gates the render too: `WSForm` captures its defaults once,
  // so mounting before the schedule arrives would show the payer the Monthly
  // fallback as though it were their saved setting.
  if (queryPayerSettings.isLoading || querySchedules.isLoading) {
    return <WSLoader.Spinner />;
  }

  if (queryPayerSettings.error) {
    return (
      <ErrorAlert context="PayrollSettings" error={queryPayerSettings.error} />
    );
  }

  if (!payrollSettings?.runsPayroll) {
    return (
      <WSFlexBox direction="column" alignItems="center" alignContent="center">
        <EmptyState
          mb="M"
          image="NoResults"
          title="No upcoming payroll runs"
          description="Enable payroll to set up payroll runs"
        />

        <WSButton
          onClick={() => setRunsPayroll(true)}
          loading={updatePayerSettingsMeta.isLoading}
        >
          Enable payroll
        </WSButton>

        <ErrorAlert
          mt="M"
          context="PayrollSettings"
          error={updatePayerSettingsMeta.error}
        />
      </WSFlexBox>
    );
  }

  // `cutOffDate` is optional on a v3 schedule date while `date` is not, so a run
  // returned without one is still upcoming — falling back to the pay date keeps
  // it in the list instead of dropping it silently.
  const now = new Date();
  const upcomingRuns = (schedule?.scheduleDates || [])
    .filter(
      scheduleDate =>
        (scheduleDate.cutOffDate
          ? getExactCutOffDate(scheduleDate.cutOffDate)
          : scheduleDate.date) > now
    )
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  return (
    <WSList gap="2XL">
      {!payrollSettings.defaultFundingSource && (
        <WSAlert
          theme="warning"
          icon="alert"
          title="Funding method is missing"
          action={{
            text: "Add funding method",
            onClick: () => {
              history.push(PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD, {
                backPath: location.pathname
              });
            }
          }}
        >
          Please add a funding method to enable payroll
        </WSAlert>
      )}

      <WSForm<PayrollCadenceFormData>
        onSubmit={handleSubmit}
        defaultValues={selectorPayrollCadenceFormData({
          schedule,
          derivedCadence
        })}
        validationSchema={Yup.object().shape(payrollCadenceValidationSchema)}
      >
        <WSList gap="XL">
          <WSElement>
            <WSText.ParagraphSm weight="medium">
              Expected pay date
            </WSText.ParagraphSm>

            {/* Only warn once both sources are gone. With the cadence resource
                  down but schedule dates present, the derivation gives the payer
                  their real setting and there is nothing to caveat; with neither,
                  the radios would otherwise present the "Monthly" fallback as
                  though it were saved, which is a confident lie. */}
            {!!querySchedules.error && !derivedCadence && (
              <WSAlert mt="M" theme="warning" icon="alert">
                Your payroll cadence can't be loaded right now, so the selection
                below isn't your saved setting. Saving will still apply the
                cadence you choose.
              </WSAlert>
            )}

            <FieldsPayrollCadence
              mt="M"
              label="How often should your contractors receive payments?"
            />
          </WSElement>

          <ErrorAlert
            context="PayrollSettings"
            error={updateScheduleMeta.error}
          />
          <ErrorAlert
            context="PayrollSettings"
            error={updatePayerSettingsMeta.error}
          />

          <WSActions
            alignment="left"
            buttons={[
              {
                label: "Save",
                loading: updateScheduleMeta.isLoading
              },
              {
                kind: "Secondary",
                destructive: true,
                type: "button",
                label: "Disable payroll",
                loading: updatePayerSettingsMeta.isLoading,
                onClick: async () => {
                  if (await modalDisablePayroll.open({})) {
                    await setRunsPayroll(false);
                  }
                }
              }
            ]}
          />
        </WSList>
      </WSForm>

      <WSGrid>
        <WSGrid.Item span={{ m: "8" }}>
          <WSText.Heading5 mb="XL">Upcoming payroll runs</WSText.Heading5>

          <WSTable<RecurrenceScheduleDate>
            tableData={upcomingRuns.map((scheduleDate, index) => ({
              id: String(index),
              data: scheduleDate
            }))}
            columns={[
              {
                config: { header: { text: "Processing date" } },
                renderFunction: ({ data }) => (
                  <WSTableCell
                    text={
                      data.cutOffDate
                        ? toWSDateString(data.cutOffDate, "monthDayYear")
                        : "—"
                    }
                  />
                )
              },
              {
                config: { header: { text: "Expected pay date" } },
                renderFunction: ({ data }) => (
                  <WSTableCell
                    text={toWSDateString(data.date, "monthDayYear")}
                  />
                )
              }
            ]}
          />
        </WSGrid.Item>
      </WSGrid>
    </WSList>
  );
};
